/**
 * BomTreeCore — shared tree-layout + pan/zoom + expand-state primitive.
 *
 * Both the BOM Explore view (every-product tree from `manufacturing.boms`)
 * and the Simulate view (per-order shortfall walk from `/api/manufacturing/
 * simulate`) want the same 80% of behaviour:
 *
 *   - Recursive tree layout that lays children out vertically per depth
 *     band with parents centered on their visible-children mid-point.
 *   - Pan via mouse-drag, zoom via wheel (non-passive listener so
 *     preventDefault() works), reset-view button.
 *   - Expand / collapse per node, with expand-all and collapse-all controls.
 *   - Bottom-right info chip with current zoom + visible-node count.
 *
 * The bits that differ are the per-node SVG body, the edge style (plain vs
 * pulse-on-non-green), and the click-behaviour of a node. Those are
 * controlled by the wrapper via render props.
 *
 * The component is generic over NodeT — BomTreeExplore passes its own
 * AssemblyNode and BomTreeSimulate passes SimulationNode. The contract is
 * `keyOf(node)` returns a stable unique-per-node string and `childrenOf
 * (node)` returns its child nodes.
 */

import {
  forwardRef,
  ReactNode,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';

export interface BomTreeCoreDims {
  nodeW: number;
  nodeH: number;
  xGap: number;
  yGap: number;
  pad: number;
}

const DEFAULT_DIMS: BomTreeCoreDims = {
  nodeW: 230,
  nodeH: 64,
  xGap: 90,
  yGap: 18,
  pad: 16,
};

export interface NodePosition {
  x: number;
  y: number;
}

export interface BomTreeRenderEdgeArgs<NodeT> {
  parent: NodeT;
  child: NodeT;
  parentPos: NodePosition;
  childPos: NodePosition;
  edgePath: string;
}

export interface BomTreeRenderNodeArgs<NodeT> {
  node: NodeT;
  pos: NodePosition;
  depth: number;
  isExpanded: boolean;
  hasChildren: boolean;
  onToggle: () => void;
}

export interface BomTreeCoreProps<NodeT> {
  root: NodeT | null;
  childrenOf: (node: NodeT) => NodeT[];
  keyOf: (node: NodeT) => string;
  renderNode: (args: BomTreeRenderNodeArgs<NodeT>) => ReactNode;
  /** Optional edge override; default draws a muted slate cubic bezier. */
  renderEdge?: (args: BomTreeRenderEdgeArgs<NodeT>) => ReactNode;
  /**
   * Initial expand state. Resets whenever `root` identity changes. Pass
   * `'root'` to pre-expand just the root node (default), or a Set of
   * keys to drive a custom initial frontier.
   */
  initialExpanded?: 'root' | Set<string>;
  /** Optional class name applied to the inner <g> wrapping all rendered
   * nodes + edges; useful to scope CSS animations (e.g. pulse keyframes). */
  innerClassName?: string;
  /** Height of the pan/zoom canvas. */
  height?: string | number;
  /** Per-tree dimension overrides — defaults match the Explore view. */
  dims?: Partial<BomTreeCoreDims>;
  /** Optional empty-state message when `root` is null. Caller controls. */
  emptyState?: ReactNode;
}

export interface BomTreeCoreHandle {
  expandAll: () => void;
  collapseAll: () => void;
  resetView: () => void;
  /** True iff there is a root and at least one expandable child anywhere. */
  hasTree: boolean;
}

type LayoutMap = Map<string, NodePosition>;

function buildEdgePath(parent: NodePosition, child: NodePosition, nodeW: number, nodeH: number) {
  const x1 = parent.x + nodeW;
  const y1 = parent.y + nodeH / 2;
  const x2 = child.x;
  const y2 = child.y + nodeH / 2;
  const cx = x1 + (x2 - x1) * 0.5;
  return `M ${x1} ${y1} C ${cx} ${y1}, ${cx} ${y2}, ${x2} ${y2}`;
}

/**
 * Inner component bound to a non-null root. Splitting this out keeps the
 * hook order stable: every hook below runs unconditionally for the lifetime
 * of a given root.
 */
interface InnerProps<NodeT> {
  root: NodeT;
  childrenOf: (node: NodeT) => NodeT[];
  keyOf: (node: NodeT) => string;
  renderNode: (args: BomTreeRenderNodeArgs<NodeT>) => ReactNode;
  renderEdge?: (args: BomTreeRenderEdgeArgs<NodeT>) => ReactNode;
  initialExpanded: 'root' | Set<string>;
  innerClassName?: string;
  height: string | number;
  dims: BomTreeCoreDims;
  exposeHandle: (h: BomTreeCoreHandle) => void;
}

function BomTreeCoreInner<NodeT>({
  root,
  childrenOf,
  keyOf,
  renderNode,
  renderEdge,
  initialExpanded,
  innerClassName,
  height,
  dims,
  exposeHandle,
}: InnerProps<NodeT>) {
  const { nodeW, nodeH, xGap, yGap, pad } = dims;
  const rootKey = keyOf(root);

  const [expanded, setExpanded] = useState<Set<string>>(() =>
    initialExpanded === 'root' ? new Set([rootKey]) : new Set(initialExpanded)
  );

  // Reset expansion when the root identity changes (new tree loaded).
  useEffect(() => {
    setExpanded(initialExpanded === 'root' ? new Set([rootKey]) : new Set(initialExpanded));
    // `initialExpanded` intentionally not in deps — callers typically pass
    // `'root'` literal; if they pass a fresh Set on every render we'd
    // thrash. Caller can force-reset by changing the root key.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rootKey]);

  // Walk every node so expandAll / collapseAll / layout know what exists.
  const allKeys = useMemo(() => {
    const keys = new Set<string>();
    const stack: NodeT[] = [root];
    while (stack.length > 0) {
      const n = stack.pop()!;
      keys.add(keyOf(n));
      for (const c of childrenOf(n)) stack.push(c);
    }
    return keys;
  }, [root, childrenOf, keyOf]);

  const { flat, layout } = useMemo(() => {
    const layoutMap: LayoutMap = new Map();
    const out: Array<{ n: NodeT; depth: number }> = [];

    const slot = { next: 0 };
    const layoutVisible = (node: NodeT, depth: number): { top: number; bottom: number } => {
      const key = keyOf(node);
      const x = pad + depth * (nodeW + xGap);
      const visibleChildren = expanded.has(key) ? childrenOf(node) : [];
      if (visibleChildren.length === 0) {
        const idx = slot.next++;
        const y = pad + idx * (nodeH + yGap);
        layoutMap.set(key, { x, y });
        return { top: y, bottom: y + nodeH };
      }
      let top = Infinity;
      let bottom = -Infinity;
      for (const c of visibleChildren) {
        const ext = layoutVisible(c, depth + 1);
        if (ext.top < top) top = ext.top;
        if (ext.bottom > bottom) bottom = ext.bottom;
      }
      const y = (top + bottom) / 2 - nodeH / 2;
      layoutMap.set(key, { x, y });
      return { top, bottom };
    };

    const flatten = (node: NodeT, depth: number) => {
      out.push({ n: node, depth });
      if (!expanded.has(keyOf(node))) return;
      for (const c of childrenOf(node)) flatten(c, depth + 1);
    };

    layoutVisible(root, 0);
    flatten(root, 0);
    return { flat: out, layout: layoutMap };
  }, [root, expanded, childrenOf, keyOf, pad, nodeW, nodeH, xGap, yGap]);

  // Pan + zoom.
  const [view, setView] = useState({ tx: 0, ty: 0, scale: 1 });
  const containerRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<{ startX: number; startY: number; tx: number; ty: number } | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  // Wheel listener needs to be non-passive so preventDefault() works to
  // stop the page from scrolling during a zoom gesture.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const handler = (e: WheelEvent) => {
      e.preventDefault();
      const delta = -e.deltaY * 0.0015;
      setView((v) => {
        const next = Math.max(0.25, Math.min(4, v.scale * (1 + delta)));
        return { ...v, scale: next };
      });
    };
    el.addEventListener('wheel', handler, { passive: false });
    return () => el.removeEventListener('wheel', handler);
  }, []);

  const onMouseDown = (e: React.MouseEvent) => {
    dragRef.current = { startX: e.clientX, startY: e.clientY, tx: view.tx, ty: view.ty };
    setIsDragging(true);
  };
  const onMouseMove = (e: React.MouseEvent) => {
    const d = dragRef.current;
    if (!d) return;
    // Defensive: if the user released the mouse outside the window the
    // next mousemove arrives with no buttons held.
    if (e.buttons === 0) {
      endDrag();
      return;
    }
    setView((v) => ({
      ...v,
      tx: d.tx + (e.clientX - d.startX),
      ty: d.ty + (e.clientY - d.startY),
    }));
  };
  const endDrag = () => {
    dragRef.current = null;
    setIsDragging(false);
  };

  const onToggle = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  // Expose imperative controls so the parent can wire its own toolbar
  // (e.g. Simulate view puts the controls in the report header). Inlined
  // so the handle's `expandAll` always reads the *current* `allKeys` —
  // capturing a top-level closure would freeze it to the snapshot at the
  // time the effect last ran.
  useEffect(() => {
    exposeHandle({
      expandAll: () => setExpanded(new Set(allKeys)),
      collapseAll: () => setExpanded(new Set([rootKey])),
      resetView: () => setView({ tx: 0, ty: 0, scale: 1 }),
      hasTree: allKeys.size > 1,
    });
  }, [allKeys, rootKey, exposeHandle]);

  // Pre-compute edges in render-order so a child without a layout entry
  // (defensive — shouldn't happen) doesn't break the SVG.
  const edges: ReactNode[] = [];
  const walkEdges = (n: NodeT) => {
    const key = keyOf(n);
    if (!expanded.has(key)) return;
    const parentPos = layout.get(key);
    if (!parentPos) return;
    for (const c of childrenOf(n)) {
      const childKey = keyOf(c);
      const childPos = layout.get(childKey);
      if (!childPos) continue;
      const path = buildEdgePath(parentPos, childPos, nodeW, nodeH);
      const edgeNode = renderEdge ? (
        renderEdge({ parent: n, child: c, parentPos, childPos, edgePath: path })
      ) : (
        <path
          key={`edge-${key}-${childKey}`}
          d={path}
          fill="none"
          stroke="#94a3b8"
          strokeWidth={1.5}
          strokeOpacity={0.7}
        />
      );
      edges.push(edgeNode);
      walkEdges(c);
    }
  };
  walkEdges(root);

  return (
    <Box
      ref={containerRef}
      sx={{
        height,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: '#f8fafc',
        position: 'relative',
        cursor: isDragging ? 'grabbing' : 'grab',
        userSelect: 'none',
      }}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={endDrag}
      onMouseLeave={endDrag}
      data-testid="bom-tree-core"
    >
      <svg width="100%" height="100%" style={{ display: 'block' }}>
        <g transform={`translate(${view.tx}, ${view.ty}) scale(${view.scale})`}>
          <g className={innerClassName}>
            <g>{edges}</g>
            <g>
              {flat.map(({ n, depth }) => {
                const key = keyOf(n);
                const pos = layout.get(key);
                if (!pos) return null;
                const kids = childrenOf(n);
                return (
                  <g key={key}>
                    {renderNode({
                      node: n,
                      pos,
                      depth,
                      isExpanded: expanded.has(key),
                      hasChildren: kids.length > 0,
                      onToggle: () => onToggle(key),
                    })}
                  </g>
                );
              })}
            </g>
          </g>
        </g>
      </svg>
      <Box
        sx={{
          position: 'absolute',
          right: 8,
          bottom: 8,
          bgcolor: 'rgba(255,255,255,0.85)',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          px: 1,
          py: 0.25,
          fontSize: 11,
          color: 'text.secondary',
          fontFamily: 'monospace',
        }}
      >
        {Math.round(view.scale * 100)}% • {flat.length} visible
      </Box>
    </Box>
  );
}

/**
 * Public component. Uses `forwardRef` so the wrapper can grab the
 * imperative handle (expand-all / collapse-all / reset-view) and render
 * its own toolbar wherever it likes.
 *
 * Implementation note: forwardRef does not preserve generics directly.
 * We cast through a typed factory to keep call sites type-safe.
 */
function BomTreeCoreImpl<NodeT>(props: BomTreeCoreProps<NodeT>, ref: React.Ref<BomTreeCoreHandle>) {
  const {
    root,
    childrenOf,
    keyOf,
    renderNode,
    renderEdge,
    initialExpanded = 'root',
    innerClassName,
    height = '70vh',
    dims: dimsOverride,
    emptyState,
  } = props;

  const dims = useMemo<BomTreeCoreDims>(
    () => ({ ...DEFAULT_DIMS, ...dimsOverride }),
    [dimsOverride]
  );

  // The imperative handle lives in a ref so we can update it from the
  // inner component once the layout has run.
  const handleRef = useRef<BomTreeCoreHandle>({
    expandAll: () => undefined,
    collapseAll: () => undefined,
    resetView: () => undefined,
    hasTree: false,
  });

  useImperativeHandle(ref, () => handleRef.current);

  // Stable identity — the inner component depends on this in a useEffect,
  // so re-creating it every render would re-fire the publish step on every
  // unrelated rerender.
  const setHandle = useCallback((h: BomTreeCoreHandle) => {
    handleRef.current = h;
  }, []);

  if (!root) {
    return <>{emptyState ?? null}</>;
  }

  return (
    <BomTreeCoreInner
      root={root}
      childrenOf={childrenOf}
      keyOf={keyOf}
      renderNode={renderNode}
      renderEdge={renderEdge}
      initialExpanded={initialExpanded}
      innerClassName={innerClassName}
      height={height}
      dims={dims}
      exposeHandle={setHandle}
    />
  );
}

// React's forwardRef typing collapses generics. Cast through the typed
// signature below to keep wrapper call sites generic over their NodeT.
export const BomTreeCore = forwardRef(BomTreeCoreImpl) as <NodeT>(
  props: BomTreeCoreProps<NodeT> & { ref?: React.Ref<BomTreeCoreHandle> }
) => ReturnType<typeof BomTreeCoreImpl>;

/**
 * Convenience toolbar: expand-all + collapse-all + reset-view icon buttons
 * wired to a BomTreeCore handle ref. Both wrappers can use this so the UX
 * is identical between Explore and Simulate modes.
 */
export function BomTreeToolbar({
  handleRef,
  disabled,
}: {
  handleRef: React.RefObject<BomTreeCoreHandle>;
  disabled?: boolean;
}) {
  const isDisabled = disabled ?? false;
  return (
    <>
      <Tooltip title="Expand all assemblies">
        <span>
          <IconButton
            size="small"
            onClick={() => handleRef.current?.expandAll()}
            disabled={isDisabled}
            data-testid="bom-tree-expand-all"
          >
            <UnfoldMoreIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Collapse to root">
        <span>
          <IconButton
            size="small"
            onClick={() => handleRef.current?.collapseAll()}
            disabled={isDisabled}
            data-testid="bom-tree-collapse-all"
          >
            <UnfoldLessIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Reset zoom + pan">
        <span>
          <IconButton
            size="small"
            onClick={() => handleRef.current?.resetView()}
            disabled={isDisabled}
            data-testid="bom-tree-reset-view"
          >
            <RestartAltIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </>
  );
}
